import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  CellValueChangedEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  PaginationChangedEvent,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Delete, Edit, Search as SearchIcon, SkipNext, ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import { TABLE_FONT_FAMILY } from '../theme';
import { ClearGridFiltersButton, useColumnFiltersActive } from './ClearGridFiltersButton';
import { Client, ClientKind, Office, Opportunity, OpportunityPipelineStatus, User } from '../types';
import ContactKindChip from './ContactKindChip';
import LastNotePreview from './LastNotePreview';
import OpportunityFormFields from './OpportunityFormFields';
import FormValidationAlert from './FormValidationAlert';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import opportunitiesService from '../services/opportunitiesService';
import { localStore } from '../utils/localStore';
import {
  OPPORTUNITY_STATUS_LABELS,
  getOpportunityStatusLabel,
  isPipelineStatus,
} from '../utils/commercialPlanning';
import {
  LEADS_DEFAULT_HIDDEN_COLUMNS,
  LEADS_TABLE_HIDDEN_COLUMNS_KEY,
  LEADS_TABLE_STATE_KEY,
  OPPORTUNITY_COLUMN_LABELS,
  OPPORTUNITY_COLUMN_PICKER_LABELS,
  OPPORTUNITY_GRID_COLUMNS,
  OPPORTUNITY_STATUS_FILTERS,
  LEAD_ACTION_LABELS,
  LEAD_ACTIONS_MAX_WIDTH,
  LeadGridRow,
  LeadsUrlFilters,
  OpportunityGridColumnKey,
  OpportunityStatusFilter,
  buildLeadGridRows,
  NEXT_ACTION_HEALTH_LABELS,
  compareLeadsByNextAction,
  formatNextActionCell,
  formatOpportunityDate,
  getOpportunityStatusFilterLabel,
  leadActionIds,
  leadActionsColumnWidth,
  leadRowMatchesLeadId,
  leadRowMatchesQuickFilter,
  leadRowToOpportunity,
  nextActionHealth,
  opportunityToInput,
  opportunityToLeadRow,
  sortLeadGridRows,
  readLeadsUrlFilters,
  writeLeadsUrlFilters,
} from '../utils/opportunityTable';
import { latestNotesCommentPreview } from '../utils/opportunityNotes';
import {
  OpportunityFormValues,
  buildNextStepForm,
  getNextStepFieldKeys,
  getNextStepTargetStatus,
  getStagesForFieldKeys,
  validateOpportunityForm,
} from '../utils/opportunityForm';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const HEADER_HEIGHT = 42;
const ROW_HEIGHT = 44;
const PAGING_PANEL_HEIGHT = 48;
const DEFAULT_LEADS_PAGE_SIZE = 20;
const LEADS_PAGE_SIZES = [10, 20, 50, 100];

const PROGRAMMATIC_COLUMN_RESIZE_SOURCES = new Set([
  'api',
  'sizeColumnsToFit',
  'autosizeColumns',
  'gridOptionsChanged',
  'flex',
]);

type LeadsTablePersistedState = {
  columnState?: ColumnState[];
};

function hiddenColumnsFromState(columnState?: ColumnState[]): string[] {
  if (!columnState?.length) return LEADS_DEFAULT_HIDDEN_COLUMNS;
  return columnState
    .filter((column) => column.hide && column.colId && column.colId !== 'actions')
    .map((column) => column.colId as string);
}

type OpportunitiesInlineTableProps = {
  opportunities: Opportunity[];
  clients: Client[];
  salesUsers: User[];
  offices: Office[];
  canCreateProject: boolean;
  onEdit: (opportunity: Opportunity) => void;
  onUpdated: (opportunity: Opportunity) => void;
  onDeleted: (id: string) => void;
  onError: (message: string) => void;
  onCreateProposal: (opportunity: Opportunity) => void;
  onCreateProject: (opportunity: Opportunity) => void;
  onCreateLeadFromLost: (opportunity: Opportunity) => void;
  onOpenDocuments: (projectId: number) => void;
  onNextStepSave: (
    opportunity: Opportunity,
    targetStatus: OpportunityPipelineStatus,
    form: OpportunityFormValues,
  ) => Promise<void>;
  findExistingClientByName: (clients: Client[], candidateName: string) => Client | undefined;
};

type GridContext = {
  savingIds: Set<string>;
  clients: Client[];
  canCreateProject: boolean;
  onEdit: (opportunity: Opportunity) => void;
  onDelete: (id: string) => void;
  onNextStep: (opportunity: Opportunity) => void;
  onCreateProposal: (opportunity: Opportunity) => void;
  onCreateProject: (opportunity: Opportunity) => void;
  onCreateLeadFromLost: (opportunity: Opportunity) => void;
  onOpenDocuments: (projectId: number) => void;
};

const contactKind = (client: Client): ClientKind => client.kind ?? 'client';

function LastNoteCellRenderer(params: { data?: LeadGridRow }) {
  if (params.data?.rowKind !== 'lead') return null;
  return <LastNotePreview notes={params.data.opportunity?.notes} />;
}

const PICKER_COLUMNS = OPPORTUNITY_GRID_COLUMNS.filter(
  (key): key is Exclude<OpportunityGridColumnKey, 'actions'> => key !== 'actions',
);

function TitleCellRenderer(params: { data?: LeadGridRow }) {
  const row = params.data;
  if (!row) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0, width: '100%' }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0, flex: 1 }}>
        {row.title}
      </Typography>
    </Box>
  );
}

function ClientContactCellRenderer(params: { value?: number; context?: GridContext }) {
  const client = params.context?.clients.find((item) => item.id === params.value);
  if (!client) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
        {client.name}
      </Typography>
      <ContactKindChip kind={contactKind(client)} />
    </Box>
  );
}

const HEALTH_COLORS: Record<'green' | 'yellow' | 'red' | 'grey', { color: string; label: string }> = {
  green: { color: '#2e7d32', label: NEXT_ACTION_HEALTH_LABELS.green },
  yellow: { color: '#ed6c02', label: NEXT_ACTION_HEALTH_LABELS.yellow },
  red: { color: '#d32f2f', label: NEXT_ACTION_HEALTH_LABELS.red },
  grey: { color: '#9e9e9e', label: NEXT_ACTION_HEALTH_LABELS.grey },
};

function NextActionHealthCellRenderer(params: { data?: LeadGridRow }) {
  if (params.data?.rowKind !== 'lead') return null;
  const health = nextActionHealth(params.data?.next_action_date, params.data?.status);
  if (health === 'none') return null;
  const { color, label } = HEALTH_COLORS[health];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <Tooltip title={label}>
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: '50%',
            bgcolor: color,
            boxShadow: `0 0 0 2px ${color}33`,
          }}
        />
      </Tooltip>
    </Box>
  );
}

function ActionsCellRenderer(params: {
  data?: LeadGridRow;
  context?: GridContext;
}) {
  const row = params.data;
  const context = params.context;
  if (!row?.id || !context) return null;
  if (row.rowKind !== 'lead' || !row.opportunity) return null;

  const opportunity = row.opportunity;
  const actions = leadActionIds(opportunity, context.canCreateProject);

  if (context.savingIds.has(opportunity.id)) {
    return <CircularProgress size={18} color="secondary" />;
  }

  const textButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.7rem',
    minWidth: 0,
    px: 1,
    py: 0.25,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box
      data-lead-actions
      onMouseDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}
    >
      {actions.includes('createProposal') && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => context.onCreateProposal(opportunity)}
          sx={{ ...textButtonSx, boxShadow: 'none' }}
        >
          {LEAD_ACTION_LABELS.createProposal}
        </Button>
      )}
      {actions.includes('openProposal') && opportunity.proposal_project_id && (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => context.onOpenDocuments(opportunity.proposal_project_id as number)}
          sx={textButtonSx}
        >
          {LEAD_ACTION_LABELS.openProposal}
        </Button>
      )}
      {actions.includes('createProject') && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => context.onCreateProject(opportunity)}
          sx={{ ...textButtonSx, boxShadow: 'none' }}
        >
          {LEAD_ACTION_LABELS.createProject}
        </Button>
      )}
      {actions.includes('createLead') && (
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => context.onCreateLeadFromLost(opportunity)}
          sx={{ ...textButtonSx, boxShadow: 'none' }}
        >
          {LEAD_ACTION_LABELS.createLead}
        </Button>
      )}
      {actions.includes('nextStep') && (
        <Tooltip title="Next step">
          <IconButton size="small" color="secondary" onClick={() => context.onNextStep(opportunity)}>
            <SkipNext fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {actions.includes('edit') && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => context.onEdit(opportunity)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {actions.includes('delete') && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={() => context.onDelete(opportunity.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const OpportunitiesInlineTable: React.FC<OpportunitiesInlineTableProps> = ({
  opportunities,
  clients,
  salesUsers,
  offices,
  canCreateProject,
  onEdit,
  onUpdated,
  onDeleted,
  onError,
  onCreateProposal,
  onCreateProject,
  onCreateLeadFromLost,
  onOpenDocuments,
  onNextStepSave,
  findExistingClientByName,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const surfaceBg = isDark ? '#0f2134' : '#ffffff';
  const headerBg = isDark ? '#132436' : '#fafafa';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';

  const [searchParams, setSearchParams] = useSearchParams();
  const { status: statusFilter, query: quickFilter, leadId: focusedLeadId } = useMemo(
    () => readLeadsUrlFilters(searchParams),
    [searchParams],
  );
  const updateUrlFilters = useCallback(
    (changes: Partial<LeadsUrlFilters>) => {
      setSearchParams((prev) => writeLeadsUrlFilters(prev, changes), { replace: true });
    },
    [setSearchParams],
  );
  const [pageSize, setPageSize] = useState(DEFAULT_LEADS_PAGE_SIZE);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(LEADS_DEFAULT_HIDDEN_COLUMNS);
  const [colVisAnchorEl, setColVisAnchorEl] = useState<HTMLElement | null>(null);
  const [rows, setRows] = useState<LeadGridRow[]>(() =>
    buildLeadGridRows(opportunities),
  );
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const [nextStepOpportunity, setNextStepOpportunity] = useState<Opportunity | null>(null);
  const [nextStepForm, setNextStepForm] = useState<OpportunityFormValues | null>(null);
  const [nextStepOutcome, setNextStepOutcome] = useState<'won' | 'lost' | 'other' | ''>('');
  const [nextStepCompanyInput, setNextStepCompanyInput] = useState('');
  const [nextStepSaving, setNextStepSaving] = useState(false);
  const [notesFullscreen, setNotesFullscreen] = useState(false);
  const fieldErrors = useFormFieldErrors();
  const rowsRef = useRef(rows);
  const suppressSaveRef = useRef(false);
  const gridRef = useRef<AgGridReact<LeadGridRow>>(null);
  const [gridApi, setGridApi] = useState<GridApi<LeadGridRow> | null>(null);
  const { columnFiltersActive, syncColumnFilters, clearColumnFilters } = useColumnFiltersActive();
  const savedTableStateRef = useRef<LeadsTablePersistedState | null>(null);
  const savedStateLoadedRef = useRef(false);
  const isApplyingSavedStateRef = useRef(false);
  const hasSavedColumnWidthsRef = useRef(false);
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  rowsRef.current = rows;

  const applySavedColumnStateToApi = useCallback((api: GridApi<LeadGridRow>, applyOrder = false) => {
    const savedState = savedTableStateRef.current?.columnState;
    if (!savedState?.length) return;

    const hasSavedSort = savedState.some(
      (columnState) => columnState.sort === 'asc' || columnState.sort === 'desc',
    );
    const state = savedState.map((columnState) => {
      const next = {
        ...columnState,
        flex: columnState.colId === 'actions' ? null : columnState.flex ?? null,
      };
      if (columnState.colId === 'actions') {
        delete next.width;
      }
      // Older saved layouts store sort: null on every column. Dropping that
      // leaves the Next Action default in place. An explicit saved sort still applies.
      if (!hasSavedSort) {
        delete next.sort;
        delete next.sortIndex;
      }
      return next;
    });

    isApplyingSavedStateRef.current = true;
    try {
      api.applyColumnState({ state, applyOrder });
      hasSavedColumnWidthsRef.current = savedState.some(
        (columnState) => typeof columnState.width === 'number' && columnState.width > 0,
      );
    } finally {
      window.setTimeout(() => {
        isApplyingSavedStateRef.current = false;
      }, 0);
    }
  }, []);

  const loadPersistedTableState = useCallback(async (): Promise<LeadsTablePersistedState | null> => {
    try {
      const savedState = await localStore.get(LEADS_TABLE_STATE_KEY) as LeadsTablePersistedState | undefined;
      if (savedState && typeof savedState === 'object' && Array.isArray(savedState.columnState)) {
        return savedState;
      }
      const legacyHidden = await localStore.get(LEADS_TABLE_HIDDEN_COLUMNS_KEY);
      if (Array.isArray(legacyHidden)) {
        const hidden = new Set(
          legacyHidden.filter((id): id is string => typeof id === 'string' && id !== 'actions'),
        );
        return {
          columnState: OPPORTUNITY_GRID_COLUMNS.map((colId) => ({
            colId,
            hide: hidden.has(colId),
          })),
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const persistHiddenColumnsState = useCallback((hidden: string[]) => {
    const existing = savedTableStateRef.current?.columnState ?? [];
    const byId = new Map(existing.map((column) => [column.colId, column]));
    const columnState: ColumnState[] = OPPORTUNITY_GRID_COLUMNS.map((colId) => ({
      ...byId.get(colId),
      colId,
      hide: hidden.includes(colId),
    }));
    const state: LeadsTablePersistedState = { columnState };
    savedTableStateRef.current = state;
    void localStore.set(LEADS_TABLE_STATE_KEY, state);
  }, []);

  const persistTableState = useCallback((api: GridApi<LeadGridRow> | null = gridApi) => {
    if (!api || isApplyingSavedStateRef.current) {
      return;
    }
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }
    persistTimeoutRef.current = setTimeout(async () => {
      try {
        const columnState = api.getColumnState();
        const state: LeadsTablePersistedState = { columnState };
        savedTableStateRef.current = state;
        await localStore.set(LEADS_TABLE_STATE_KEY, state);
        hasSavedColumnWidthsRef.current = Boolean(
          columnState.some((column) => typeof column.width === 'number' && column.width > 0),
        );
        const hidden = hiddenColumnsFromState(columnState);
        setHiddenColumns(hidden);
      } catch {
        // Ignore persistence failures (private mode / blocked storage).
      }
      persistTimeoutRef.current = null;
    }, 400);
  }, [gridApi]);

  const toggleColumn = useCallback(
    (colId: string, visible: boolean) => {
      if (colId === 'actions') return;
      const next = visible
        ? hiddenColumns.filter((id) => id !== colId)
        : [...hiddenColumns, colId];
      setHiddenColumns(next);
      const api = gridRef.current?.api ?? gridApi;
      if (api) {
        api.setColumnsVisible([colId], visible);
        persistTableState(api);
      } else {
        persistHiddenColumnsState(next);
      }
    },
    [gridApi, hiddenColumns, persistHiddenColumnsState, persistTableState],
  );

  const hiddenColumnSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const autoSizeColumns = useCallback(() => {
    if (hasSavedColumnWidthsRef.current) return;
    gridRef.current?.api?.autoSizeAllColumns(false);
  }, []);

  const onGridReady = useCallback(async (params: GridReadyEvent<LeadGridRow>) => {
    try {
      if (!savedStateLoadedRef.current) {
        const savedState = await loadPersistedTableState();
        if (savedState) {
          savedTableStateRef.current = savedState;
          hasSavedColumnWidthsRef.current = Boolean(
            savedState.columnState?.some(
              (columnState) => typeof columnState.width === 'number' && columnState.width > 0,
            ),
          );
          setHiddenColumns(hiddenColumnsFromState(savedState.columnState));
        }
      }
      applySavedColumnStateToApi(params.api, true);
      if (!hasSavedColumnWidthsRef.current) {
        params.api.autoSizeAllColumns(false);
      }
    } finally {
      savedStateLoadedRef.current = true;
      setGridApi(params.api);
      syncColumnFilters(params.api);
    }
  }, [applySavedColumnStateToApi, loadPersistedTableState, syncColumnFilters]);

  useEffect(() => {
    let cancelled = false;
    void loadPersistedTableState().then((savedState) => {
      if (cancelled || !savedState) return;
      savedTableStateRef.current = savedState;
      savedStateLoadedRef.current = true;
      hasSavedColumnWidthsRef.current = Boolean(
        savedState.columnState?.some(
          (columnState) => typeof columnState.width === 'number' && columnState.width > 0,
        ),
      );
      setHiddenColumns(hiddenColumnsFromState(savedState.columnState));
    });
    return () => {
      cancelled = true;
    };
  }, [loadPersistedTableState]);

  useEffect(() => {
    setRows(buildLeadGridRows(opportunities));
  }, [opportunities]);

  const filteredRows = useMemo(() => {
    const lookup = { clients, salesUsers, offices };
    const matched = rows.filter((row) => {
      if (!leadRowMatchesLeadId(row, focusedLeadId)) return false;
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      return leadRowMatchesQuickFilter(row, quickFilter, lookup);
    });
    return sortLeadGridRows(matched);
  }, [clients, focusedLeadId, offices, quickFilter, rows, salesUsers, statusFilter]);

  const actionsColumnWidth = useMemo(
    () => leadActionsColumnWidth(filteredRows, canCreateProject),
    [canCreateProject, filteredRows],
  );

  const focusedLeadLabel = useMemo(() => {
    if (!focusedLeadId) return null;
    const title = rows.find((row) => leadRowMatchesLeadId(row, focusedLeadId))?.title;
    return `Lead: ${title?.trim() || `#${focusedLeadId}`}`;
  }, [focusedLeadId, rows]);

  const countsByStatus = useMemo(() => {
    const counts: Record<OpportunityStatusFilter, number> = {
      all: rows.length,
      new: 0,
      qualified: 0,
      opportunity: 0,
      bidding: 0,
      negotiation: 0,
      won: 0,
      lost: 0,
      other: 0,
    };
    rows.forEach((row) => {
      if (isPipelineStatus(row.status)) {
        counts[row.status] += 1;
      }
    });
    return counts;
  }, [rows]);

  const persistRow = useCallback(
    async (row: LeadGridRow) => {
      const opportunity = leadRowToOpportunity(row);
      if (!opportunity) return;
      if (!opportunity.title.trim()) {
        onError('Title is required.');
        return;
      }

      setSavingIds((prev) => new Set(prev).add(opportunity.id));
      try {
        const updated = await opportunitiesService.updateOpportunity(
          opportunity.id,
          opportunityToInput(opportunity),
        );
        setRows((prev) => prev.map((item) => (
          item.id === `lead-${updated.id}` ? opportunityToLeadRow(updated) : item
        )));
        onUpdated(updated);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        onError(msg || (err instanceof Error ? err.message : 'Failed to save opportunity'));
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(opportunity.id);
          return next;
        });
      }
    },
    [onError, onUpdated],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('Delete this opportunity?')) return;
      setSavingIds((prev) => new Set(prev).add(id));
      try {
        await opportunitiesService.deleteOpportunity(id);
        setRows((prev) => prev.filter((row) => row.opportunity?.id !== id));
        onDeleted(id);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        onError(msg || (err instanceof Error ? err.message : 'Failed to delete opportunity'));
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [onDeleted, onError],
  );

  const closeNextStepDialog = () => {
    setNextStepOpen(false);
    setNextStepOpportunity(null);
    setNextStepForm(null);
    setNextStepOutcome('');
    setNextStepCompanyInput('');
    setNotesFullscreen(false);
    fieldErrors.clear();
  };

  const handleNextStepDialogClose = () => {
    if (notesFullscreen) {
      setNotesFullscreen(false);
      return;
    }
    closeNextStepDialog();
  };

  const handleOpenNextStep = useCallback((opportunity: Opportunity) => {
    if (!isPipelineStatus(opportunity.status)) return;
    const targetStatus = getNextStepTargetStatus(opportunity.status);
    if (!targetStatus && opportunity.status !== 'negotiation') return;

    setNextStepOpportunity(opportunity);
    setNextStepOutcome('');
    if (targetStatus) {
      setNextStepForm(buildNextStepForm(opportunity, targetStatus));
    } else {
      setNextStepForm(buildNextStepForm(opportunity, 'won'));
    }
    const linkedClient = opportunity.client_id
      ? clients.find((client) => client.id === opportunity.client_id)
      : null;
    setNextStepCompanyInput(linkedClient?.name || opportunity.client_name || '');
    setNextStepOpen(true);
  }, [clients]);

  const effectiveNextStepStatus = useMemo(() => {
    if (!nextStepOpportunity || !isPipelineStatus(nextStepOpportunity.status)) return null;
    if (nextStepOpportunity.status === 'negotiation') {
      return nextStepOutcome || null;
    }
    return getNextStepTargetStatus(nextStepOpportunity.status);
  }, [nextStepOpportunity, nextStepOutcome]);

  const nextStepVisibleFields = useMemo(() => {
    if (!nextStepOpportunity || !nextStepForm || !effectiveNextStepStatus) return [];
    if (!isPipelineStatus(nextStepOpportunity.status)) return [];
    return getNextStepFieldKeys(nextStepOpportunity.status, effectiveNextStepStatus, nextStepForm);
  }, [nextStepOpportunity, nextStepForm, effectiveNextStepStatus]);

  const nextStepVisibleStages = useMemo(
    () => getStagesForFieldKeys(nextStepVisibleFields),
    [nextStepVisibleFields],
  );

  const handleNextStepOutcomeChange = (outcome: 'won' | 'lost' | 'other') => {
    if (!nextStepOpportunity) return;
    setNextStepOutcome(outcome);
    setNextStepForm(buildNextStepForm(nextStepOpportunity, outcome));
  };

  const handleNextStepSubmit = async () => {
    if (!nextStepOpportunity || !nextStepForm || !effectiveNextStepStatus) return;

    const validationError = validateOpportunityForm({
      ...nextStepForm,
      status: effectiveNextStepStatus,
    });
    if (validationError) {
      fieldErrors.show(validationError.message, validationError.fields);
      return;
    }

    setNextStepSaving(true);
    try {
      await onNextStepSave(nextStepOpportunity, effectiveNextStepStatus, {
        ...nextStepForm,
        status: effectiveNextStepStatus,
      });
      closeNextStepDialog();
    } finally {
      setNextStepSaving(false);
    }
  };

  const gridContext = useMemo<GridContext>(
    () => ({
      savingIds,
      clients,
      canCreateProject,
      onEdit,
      onDelete: handleDelete,
      onNextStep: handleOpenNextStep,
      onCreateProposal,
      onCreateProject,
      onCreateLeadFromLost,
      onOpenDocuments,
    }),
    [canCreateProject, clients, handleDelete, handleOpenNextStep, onCreateLeadFromLost, onCreateProject, onCreateProposal, onEdit, onOpenDocuments, savingIds],
  );

  const onCellValueChanged = useCallback(
    async (event: CellValueChangedEvent<LeadGridRow>) => {
      if (suppressSaveRef.current || !event.data || event.data.rowKind !== 'lead') return;

      const field = event.colDef.field as keyof LeadGridRow | undefined;
      if (!field) return;

      let data: LeadGridRow = { ...event.data };

      if (field === 'commercial_owner_id') {
        const owner = salesUsers.find((user) => user.id === data.commercial_owner_id);
        data.commercial_owner_name = owner?.name;
      }

      if (field === 'office_id') {
        const office = offices.find((item) => item.id === data.office_id);
        data.office_name = office?.name || office?.code || office?.country;
      }

      const normalizedNew =
        event.newValue instanceof Date ? formatOpportunityDate(event.newValue) : event.newValue;
      const normalizedOld =
        event.oldValue instanceof Date ? formatOpportunityDate(event.oldValue) : event.oldValue;

      if (normalizedNew === normalizedOld && field !== 'client_id') return;

      if (field === 'next_action_date') {
        data.next_action_date = formatOpportunityDate(event.newValue) || undefined;
      }

      await persistRow(data);
    },
    [offices, persistRow, salesUsers],
  );

  const onRowDoubleClicked = useCallback(
    (event: RowDoubleClickedEvent<LeadGridRow>) => {
      const target = event.event?.target as HTMLElement | undefined;
      if (target?.closest('[data-lead-actions]')) return;
      const row = event.data;
      if (!row) return;
      if (row.opportunity?.proposal_project_id) {
        onOpenDocuments(row.opportunity.proposal_project_id);
      }
    },
    [onOpenDocuments],
  );

  const isLeadEditable = useCallback(
    (params: { data?: LeadGridRow }) => params.data?.rowKind === 'lead',
    [],
  );

  const columnDefs = useMemo<ColDef<LeadGridRow>[]>(() => {
    const clientValues = ['', ...clients.map((client) => client.id)];
    const userValues = ['', ...salesUsers.map((user) => user.id)];
    const officeValues = ['', ...offices.map((office) => office.id)];

    const defsByKey: Record<OpportunityGridColumnKey, ColDef<LeadGridRow>> = {
      title: {
        colId: 'title',
        field: 'title',
        headerName: OPPORTUNITY_COLUMN_LABELS.title,
        editable: isLeadEditable,
        minWidth: 220,
        flex: 1,
        cellRenderer: TitleCellRenderer,
      },
      status: {
        colId: 'status',
        field: 'status',
        headerName: OPPORTUNITY_COLUMN_LABELS.status,
        editable: false,
        minWidth: 110,
        maxWidth: 200,
        valueFormatter: (params) => getOpportunityStatusLabel(String(params.value || '')),
      },
      client: {
        colId: 'client',
        field: 'client_id',
        headerName: OPPORTUNITY_COLUMN_LABELS.client,
        editable: isLeadEditable,
        minWidth: 110,
        maxWidth: 280,
        cellRenderer: ClientContactCellRenderer,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: clientValues },
        valueFormatter: (params) => {
          if (!params.value) return '';
          const client = clients.find((item) => item.id === params.value);
          if (!client) return '';
          const kindLabel = contactKind(client) === 'lead' ? 'Lead' : 'Client';
          return `${client.name} (${kindLabel})`;
        },
        valueParser: (params) => {
          const value = params.newValue;
          if (value === '' || value === null || value === undefined) return undefined;
          return Number(value);
        },
      },
      commercial_owner: {
        colId: 'commercial_owner',
        field: 'commercial_owner_id',
        headerName: OPPORTUNITY_COLUMN_LABELS.commercial_owner,
        editable: isLeadEditable,
        minWidth: 90,
        maxWidth: 200,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: userValues },
        valueFormatter: (params) => {
          if (!params.value) return '';
          return salesUsers.find((user) => user.id === params.value)?.name || '';
        },
        valueParser: (params) => {
          const value = params.newValue;
          if (value === '' || value === null || value === undefined) return undefined;
          return Number(value);
        },
      },
      office: {
        colId: 'office',
        field: 'office_id',
        headerName: OPPORTUNITY_COLUMN_LABELS.office,
        editable: isLeadEditable,
        minWidth: 72,
        maxWidth: 160,
        hide: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: officeValues },
        valueFormatter: (params) => {
          if (!params.value) return '';
          const office = offices.find((item) => item.id === params.value);
          return office?.name || office?.code || office?.country || '';
        },
        valueParser: (params) => {
          const value = params.newValue;
          if (value === '' || value === null || value === undefined) return undefined;
          return Number(value);
        },
      },
      next_action_health: {
        colId: 'next_action_health',
        headerName: OPPORTUNITY_COLUMN_LABELS.next_action_health,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        suppressAutoSize: true,
        suppressNavigable: true,
        cellRenderer: NextActionHealthCellRenderer,
      },
      next_action_date: {
        colId: 'next_action_date',
        field: 'next_action_date',
        headerName: OPPORTUNITY_COLUMN_LABELS.next_action_date,
        editable: isLeadEditable,
        minWidth: 130,
        maxWidth: 220,
        initialSort: 'asc',
        comparator: (_valueA, _valueB, nodeA, nodeB) => {
          if (!nodeA?.data || !nodeB?.data) return 0;
          return compareLeadsByNextAction(nodeA.data, nodeB.data);
        },
        cellEditor: 'agDateStringCellEditor',
        valueFormatter: (params) => formatNextActionCell(params.value),
      },
      last_note: {
        colId: 'last_note',
        headerName: OPPORTUNITY_COLUMN_LABELS.last_note,
        editable: false,
        minWidth: 140,
        maxWidth: 420,
        valueGetter: (params) => latestNotesCommentPreview(params.data?.opportunity?.notes || ''),
        cellRenderer: LastNoteCellRenderer,
      },
      actions: {
        colId: 'actions',
        headerName: OPPORTUNITY_COLUMN_LABELS.actions,
        editable: false,
        sortable: false,
        filter: false,
        pinned: 'right',
        width: actionsColumnWidth,
        minWidth: actionsColumnWidth,
        maxWidth: Math.max(LEAD_ACTIONS_MAX_WIDTH, actionsColumnWidth),
        suppressAutoSize: true,
        suppressNavigable: true,
        cellRenderer: ActionsCellRenderer,
      },
    };

    return OPPORTUNITY_GRID_COLUMNS.map((key) => defsByKey[key]);
  }, [actionsColumnWidth, clients, isLeadEditable, offices, salesUsers]);

  useEffect(() => {
    if (!gridApi || !savedStateLoadedRef.current) return;
    if (hasSavedColumnWidthsRef.current || savedTableStateRef.current?.columnState?.length) {
      applySavedColumnStateToApi(gridApi, true);
    }
  }, [applySavedColumnStateToApi, columnDefs, gridApi]);

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;
    gridApi.setColumnWidths([{ key: 'actions', newWidth: actionsColumnWidth }]);
  }, [actionsColumnWidth, gridApi]);

  useEffect(() => {
    if (hasSavedColumnWidthsRef.current) return;
    const handle = requestAnimationFrame(() => autoSizeColumns());
    return () => cancelAnimationFrame(handle);
  }, [autoSizeColumns, statusFilter, filteredRows.length, hiddenColumns]);

  useEffect(() => () => {
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      suppressMovable: false,
    }),
    [],
  );

  const onPaginationChanged = useCallback((event: PaginationChangedEvent<LeadGridRow>) => {
    if (event.newPageSize) {
      const nextSize = event.api.paginationGetPageSize();
      if (LEADS_PAGE_SIZES.includes(nextSize)) {
        setPageSize((prev) => (prev === nextSize ? prev : nextSize));
      }
    }
  }, []);

  const gridShellSx = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: 0,
      bgcolor: surfaceBg,
      fontFamily: TABLE_FONT_FAMILY,
      '--ag-pagination-panel-height': `${PAGING_PANEL_HEIGHT}px`,
      '--ag-background-color': surfaceBg,
      '--ag-foreground-color': theme.palette.text.primary,
      '--ag-header-background-color': headerBg,
      '--ag-header-foreground-color': theme.palette.text.primary,
      '--ag-odd-row-background-color': isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      '--ag-row-hover-color': isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      '--ag-border-color': panelBorder,
      '--ag-row-border-color': panelBorder,
      '--ag-secondary-border-color': panelBorder,
      '& .ag-root-wrapper': {
        height: '100%',
        border: 'none',
        borderRadius: 0,
        backgroundColor: surfaceBg,
      },
      '& .ag-root-wrapper-body': {
        backgroundColor: surfaceBg,
      },
      '& .ag-body-viewport, & .ag-center-cols-viewport': {
        backgroundColor: surfaceBg,
      },
      '& .ag-body-horizontal-scroll': {
        borderTop: `1px solid ${panelBorder}`,
      },
      '& .ag-pinned-right-header, & .ag-pinned-right-cols-container': {
        borderLeft: `1px solid ${panelBorder}`,
      },
      '& .ag-header': {
        borderBottom: `1px solid ${panelBorder}`,
      },
      '& .ag-paging-panel': {
        borderTopColor: panelBorder,
        backgroundColor: surfaceBg,
        color: theme.palette.text.secondary,
      },
      '& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        borderRightColor: panelBorder,
      },
      '& .ag-cell-value': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        minWidth: 0,
        lineHeight: 'normal',
      },
      '& .ag-header-cell': {
        fontWeight: 700,
      },
    }),
    [headerBg, isDark, panelBorder, surfaceBg, theme.palette.text.primary],
  );

  const nextStepDialogTitle = useMemo(() => {
    if (!nextStepOpportunity || !isPipelineStatus(nextStepOpportunity.status)) return 'Next step';
    if (nextStepOpportunity.status === 'negotiation') {
      return nextStepOutcome
        ? `Close as ${OPPORTUNITY_STATUS_LABELS[nextStepOutcome]}`
        : 'Choose outcome';
    }
    const target = getNextStepTargetStatus(nextStepOpportunity.status);
    return target ? `Next step: ${OPPORTUNITY_STATUS_LABELS[target]}` : 'Next step';
  }, [nextStepOpportunity, nextStepOutcome]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${panelBorder}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <TextField
          size="small"
          placeholder="Quick filter leads..."
          value={quickFilter}
          onChange={(e) => updateUrlFilters({ query: e.target.value, leadId: null })}
          sx={{ flex: 1, maxWidth: 400, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="opportunity-status-filter-label">Status</InputLabel>
          <Select
            labelId="opportunity-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => updateUrlFilters({
              status: e.target.value as OpportunityStatusFilter,
              leadId: null,
            })}
          >
            {OPPORTUNITY_STATUS_FILTERS.map((filter) => (
              <MenuItem key={filter} value={filter}>
                {getOpportunityStatusFilterLabel(filter)} ({countsByStatus[filter]})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {focusedLeadLabel && (
          <Chip
            label={focusedLeadLabel}
            size="small"
            onDelete={() => updateUrlFilters({ leadId: null })}
            sx={{ fontWeight: 600, maxWidth: 360 }}
          />
        )}
        {columnFiltersActive && (
          <Box sx={{ ml: 'auto' }}>
            <ClearGridFiltersButton onClick={() => clearColumnFilters(gridApi)} />
          </Box>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewColumnIcon />}
          onClick={(e) => setColVisAnchorEl(e.currentTarget)}
          sx={{ ml: columnFiltersActive ? undefined : 'auto' }}
        >
          Columns
        </Button>
        <Popover
          open={Boolean(colVisAnchorEl)}
          anchorEl={colVisAnchorEl}
          onClose={() => setColVisAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto', minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Show/Hide Columns</Typography>
            <FormGroup>
              {PICKER_COLUMNS.map((colId) => (
                <FormControlLabel
                  key={colId}
                  control={(
                    <Checkbox
                      checked={!hiddenColumnSet.has(colId)}
                      onChange={(e) => toggleColumn(colId, e.target.checked)}
                      size="small"
                    />
                  )}
                  label={OPPORTUNITY_COLUMN_PICKER_LABELS[colId]}
                />
              ))}
            </FormGroup>
          </Box>
        </Popover>
      </Box>

      {filteredRows.length === 0 ? (
        <Box py={4} px={2} textAlign="center" sx={{ flex: 1, minHeight: 0 }}>
          <Typography variant="body2" color="text.secondary">
            No {statusFilter === 'all' ? '' : `${getOpportunityStatusFilterLabel(statusFilter).toLowerCase()} `}
            opportunities in this view.
          </Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
          sx={gridShellSx}
        >
          <AgGridReact<LeadGridRow>
            ref={gridRef}
            theme="legacy"
            containerStyle={{ height: '100%', width: '100%' }}
            rowData={filteredRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            context={gridContext}
            getRowId={(params) => params.data.id}
            onGridReady={onGridReady}
            onFilterChanged={(event) => syncColumnFilters(event.api)}
            onFirstDataRendered={(params) => {
              if (hasSavedColumnWidthsRef.current) {
                applySavedColumnStateToApi(params.api, true);
                return;
              }
              autoSizeColumns();
            }}
            onColumnResized={(event: ColumnResizedEvent<LeadGridRow>) => {
              if (
                !event.finished
                || !event.source
                || PROGRAMMATIC_COLUMN_RESIZE_SOURCES.has(event.source)
              ) {
                return;
              }
              persistTableState(event.api);
            }}
            onColumnMoved={(event: ColumnMovedEvent<LeadGridRow>) => {
              if (!event.finished) return;
              persistTableState(event.api);
            }}
            onCellValueChanged={onCellValueChanged}
            onRowDoubleClicked={onRowDoubleClicked}
            stopEditingWhenCellsLoseFocus
            singleClickEdit
            rowHeight={ROW_HEIGHT}
            headerHeight={HEADER_HEIGHT}
            pagination
            paginationPageSize={pageSize}
            paginationPageSizeSelector={LEADS_PAGE_SIZES}
            onPaginationChanged={onPaginationChanged}
            suppressColumnVirtualisation
            animateRows={false}
            enableCellTextSelection
          />
        </Paper>
      )}

      <Dialog
        open={nextStepOpen}
        onClose={handleNextStepDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={notesFullscreen}
        PaperProps={notesFullscreen ? { sx: { display: 'flex', flexDirection: 'column' } } : undefined}
      >
        <DialogTitle>{nextStepDialogTitle}</DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent
          sx={notesFullscreen ? {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          } : undefined}
        >
          {!notesFullscreen && nextStepOpportunity?.status === 'negotiation' && !nextStepOutcome && (
            <Box sx={{ display: 'flex', gap: 2, pt: 1 }}>
              <Button variant="contained" color="success" onClick={() => handleNextStepOutcomeChange('won')}>
                Won
              </Button>
              <Button variant="contained" color="error" onClick={() => handleNextStepOutcomeChange('lost')}>
                Lost
              </Button>
              <Button variant="contained" color="inherit" onClick={() => handleNextStepOutcomeChange('other')}>
                Other
              </Button>
            </Box>
          )}

          {nextStepForm && effectiveNextStepStatus && nextStepVisibleFields.length > 0 && (
            <Box sx={{
              pt: notesFullscreen ? 0 : 2,
              flex: notesFullscreen ? 1 : undefined,
              minHeight: 0,
              display: notesFullscreen ? 'flex' : undefined,
              flexDirection: notesFullscreen ? 'column' : undefined,
            }}>
              {!notesFullscreen && nextStepOpportunity?.status === 'negotiation' && nextStepOutcome && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Outcome: {OPPORTUNITY_STATUS_LABELS[nextStepOutcome]}
                </Typography>
              )}
              <OpportunityFormFields
                form={nextStepForm}
                setForm={setNextStepForm}
                visibleStages={nextStepVisibleStages}
                visibleFields={nextStepVisibleFields}
                clients={clients}
                salesUsers={salesUsers}
                offices={offices}
                companyInputValue={nextStepCompanyInput}
                setCompanyInputValue={setNextStepCompanyInput}
                onCompanySelection={async () => undefined}
                findExistingClientByName={findExistingClientByName}
                creatingContact={false}
                notesExpanded
                notesFullscreen={notesFullscreen}
                onToggleNotesFullscreen={() => setNotesFullscreen((prev) => !prev)}
                invalid={fieldErrors.invalid}
                onFieldEdit={fieldErrors.clearField}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNextStepDialog} disabled={nextStepSaving}>Cancel</Button>
          {effectiveNextStepStatus && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleNextStepSubmit}
              disabled={nextStepSaving}
            >
              {nextStepSaving ? 'Saving...' : 'Save & advance'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpportunitiesInlineTable;
